import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "backgroundColor", "fadeSize", "reverseScroll"];
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { WHITE } from '../constants/WidgetColors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FADE_SIZE = 20;
const Wrapper = styled.div.withConfig({
  displayName: "VizExScrollFade__Wrapper"
})(["position:relative;"]);
const Fade = styled.div.withConfig({
  displayName: "VizExScrollFade__Fade"
})(["background-image:linear-gradient( to ", ",transparent,", " );height:", "px;width:100%;opacity:", ";transition:opacity 150ms ease;pointer-events:none;position:absolute;", ""], ({
  $side
}) => $side, ({
  $color
}) => $color, ({
  $size
}) => $size, ({
  $opacity
}) => $opacity, ({
  $side
}) => $side === 'top' ? 'top: 0;' : 'bottom: 0;');
const VizExScrollFade = _ref => {
  let {
      children,
      backgroundColor = WHITE,
      fadeSize = FADE_SIZE,
      reverseScroll = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const containerRef = useRef(null);
  const [scrollable, setScrollable] = useState({
    top: 0,
    bottom: 0
  });
  const prevScrollableRef = useRef(scrollable);
  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const childEl = container.firstElementChild;
    if (!childEl) return;
    const computedStyles = getComputedStyle(childEl);
    const isScrollable = overflow => overflow === 'auto' || overflow === 'scroll';
    if (!isScrollable(computedStyles.overflowY)) return;
    const {
      clientHeight,
      scrollHeight,
      scrollTop
    } = childEl;
    const absScrollTop = reverseScroll ? Math.abs(scrollTop) : scrollTop;
    const newTop = reverseScroll ? scrollHeight - clientHeight - absScrollTop : absScrollTop;
    const newBottom = reverseScroll ? absScrollTop : scrollHeight - clientHeight - absScrollTop;
    setScrollable(prev => {
      if (prev.top === newTop && prev.bottom === newBottom) return prev;
      return {
        top: newTop,
        bottom: newBottom
      };
    });
  }, [reverseScroll]);
  useEffect(() => {
    if (prevScrollableRef.current === scrollable) {
      measure();
    }
    prevScrollableRef.current = scrollable;
  });
  useEffect(() => {
    const container = containerRef.current;
    const childEl = container === null || container === void 0 ? void 0 : container.firstElementChild;
    if (!childEl) return;
    childEl.addEventListener('scroll', measure, {
      passive: true
    });
    return () => childEl.removeEventListener('scroll', measure);
  }, [measure]);
  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);
  const getOpacity = side => Math.min((scrollable[side] || 0) / fadeSize, 1);
  return /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, rest, {
    "data-test-id": "scroll-fade-wrapper",
    ref: containerRef,
    children: [children, /*#__PURE__*/_jsx(Fade, {
      "data-test-id": "scroll-fade-top",
      $side: "top",
      $opacity: getOpacity('top'),
      $size: fadeSize,
      $color: backgroundColor
    }), /*#__PURE__*/_jsx(Fade, {
      "data-test-id": "scroll-fade-bottom",
      $side: "bottom",
      $opacity: getOpacity('bottom'),
      $size: fadeSize,
      $color: backgroundColor
    })]
  }));
};
VizExScrollFade.displayName = 'VizExScrollFade';
export default VizExScrollFade;